import React from 'react';
import { Descriptions, Result, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { IPreviewRow, ROW_LEVEL_KEY } from '../../../../utils/asset-type-import';
import { IResultStepProps } from './i-import-wizard';

const headerCellStyle: React.CSSProperties = {
  padding: '17px 10px',
  background: '#fafafa',
  fontSize: 13,
  fontWeight: 600,
  color: 'rgba(0, 0, 0, 0.85)',
};

const bodyCellStyle: React.CSSProperties = {
  padding: '8px 10px',
  fontSize: 13,
  color: '#535559',
};

const firstMessage = (bag: Record<string, string>): string => {
  if (bag[ROW_LEVEL_KEY]) return bag[ROW_LEVEL_KEY];

  const values = Object.values(bag);
  return values.length ? values[0] : '';
};

const reasonFor = (row: IPreviewRow): string => (
  row.serverMessage || firstMessage(row.errors) || firstMessage(row.warnings) || ''
);

const columns: ColumnsType<IPreviewRow> = [
  {
    title: 'Line',
    dataIndex: 'line',
    key: 'line',
    width: 80,
    onHeaderCell: () => ({ style: headerCellStyle }),
    onCell: () => ({ style: bodyCellStyle }),
  },
  {
    title: 'Code',
    dataIndex: 'code',
    key: 'code',
    width: 200,
    onHeaderCell: () => ({ style: headerCellStyle }),
    onCell: () => ({ style: bodyCellStyle }),
  },
  {
    title: 'Reason',
    key: 'reason',
    render: (_, row) => reasonFor(row),
    onHeaderCell: () => ({ style: headerCellStyle }),
    onCell: () => ({ style: bodyCellStyle }),
  },
];

const ResultStep = (props: IResultStepProps): JSX.Element => {
  const { summary, resultSummary, rows } = props;

  const failed = resultSummary?.failed ?? 0;
  const updated = resultSummary?.updated ?? 0;

  const problemRows = rows.filter(
    (row) => row.status === 'duplicate' || row.status === 'invalid' || row.status === 'failed',
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Result
        status={failed > 0 ? 'warning' : 'success'}
        title={failed > 0 ? 'Imported with errors' : 'Imported'}
        style={{ padding: '24px 0 8px' }}
      />

      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item label="Created">{resultSummary?.created ?? 0}</Descriptions.Item>
        {updated > 0 ? (
          <Descriptions.Item label="Updated">{updated}</Descriptions.Item>
        ) : null}
        <Descriptions.Item label="Skipped as duplicates">
          {resultSummary?.skipped ?? 0}
        </Descriptions.Item>
        {failed > 0 ? (
          <Descriptions.Item label="Failed">{failed}</Descriptions.Item>
        ) : null}
        <Descriptions.Item label="Invalid (not sent)">{summary.invalidCount}</Descriptions.Item>
      </Descriptions>

      <div style={{ fontSize: 13, color: '#8C909B' }}>
        The asset types exist now; the list catches up within a few seconds of closing this.
      </div>

      {problemRows.length ? (
        <div style={{ border: '1px solid #f1f2f5', borderRadius: 11, overflow: 'hidden' }}>
          <Table
            size="small"
            columns={columns}
            dataSource={problemRows}
            rowKey={(row) => row.key}
            bordered={false}
            pagination={{ pageSize: 10, size: 'small', hideOnSinglePage: true }}
          />
        </div>
      ) : null}
    </div>
  );
};

ResultStep.displayName = 'ResultStep';

export default ResultStep;
